/**
 * E3 — the anniversary re-release programme, surfaced for the first time.
 *
 * A year after the wedding, a couple who asked for it at setup is invited to
 * write a foreword and receive a second edition. The whole programme was
 * invisible here until now: the migration shipped the table and the job, and no
 * admin surface ever read either.
 *
 * DRAWN THE StatusFunnelCard WAY — hand-rolled MUI boxes over a palette key per
 * stage, not a chart library. Four proportional segments of one bar is layout,
 * not plotting, and this way both themes and any future palette change follow
 * for free with no hex to chase. (The wedding section's charts are recharts;
 * mixing in the Chart.js wrapper used elsewhere in the dashboard is the one
 * thing that must not happen here.)
 *
 * ⚠️ `declined` IS NOT AN ERROR STATE. It is a couple saying no thank you to a
 * second edition, honoured forever — the pipeline query treats ANY existing row
 * as a reason not to invite again. So it is drawn in a neutral callout rather
 * than in the red an operator would read as something to fix.
 *
 * ⚠️ `available: false` MEANS THE TABLE IS NOT ON THIS DATABASE. That is not a
 * failure a Retry could clear, so this card renders the server's reason and no
 * button — unlike a section that appears in the overview's `unavailable` list,
 * which failed and might well succeed on the next attempt.
 *
 * THE PIPELINE NUMBERS ARE ELIGIBILITY, NOT SCHEDULED SENDS, and the server's
 * `eligibility_note` says so in its own words at the foot of the card.
 */

import React from 'react';
import { Box, Card, CardContent, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import type { AnniversaryEdition, AnniversaryProgramme } from '../weddingApi';
import { shortDate } from '../weddingFormat';

interface Props {
  anniversary?: AnniversaryProgramme | null;
}

/**
 * The four flow stages, in order, each with a MUI palette key. `declined` is
 * deliberately absent: it is terminal and gets its own neutral callout.
 */
const STAGES: { key: keyof AnniversaryEdition; label: string; tone: 'info' | 'primary' | 'secondary' | 'success' }[] = [
  { key: 'invited', label: 'Invited', tone: 'info' },
  { key: 'foreword_written', label: 'Foreword written', tone: 'primary' },
  { key: 'generating', label: 'Generating', tone: 'secondary' },
  { key: 'delivered', label: 'Delivered', tone: 'success' },
];

function ratePct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

const AnniversaryProgrammeCard: React.FC<Props> = ({ anniversary }) => {
  const theme = useTheme();

  if (!anniversary) {
    return (
      <Card sx={{ height: '100%', borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Anniversary editions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The anniversary programme is not reported by this version of the admin API.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!anniversary.available) {
    return (
      <Card sx={{ height: '100%', borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Anniversary editions
          </Typography>
          {/*
            The server's own reason, and NO Retry: this is a table that is not
            in this database, not a query that happened to fail.
          */}
          <Typography variant="body2" color="text.secondary">
            {anniversary.reason || 'Not measurable on this database.'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const optIn = anniversary.opt_in;
  const editions = anniversary.editions || [];
  const pipeline = anniversary.pipeline;
  const tiers = (anniversary.denominator_tiers || []).join(', ');

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Anniversary editions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Point in time — not scoped to the date filter.
        </Typography>

        {optIn && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {optIn.opted_in.toLocaleString()} of {optIn.eligible_books.toLocaleString()} opted in
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({ratePct(optIn.opt_in_rate)})
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Measured over {tiers || 'the anniversary tiers'} books
              {optIn.opted_out === null || optIn.opted_out === undefined
                ? ' · opt-outs are not recorded on this database'
                : ` · ${optIn.opted_out.toLocaleString()} opted out`}
            </Typography>
          </Box>
        )}

        {editions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No Platinum book has reached its first anniversary yet.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {editions.map((edition) => {
              const counts = STAGES.map((stage) => ({
                ...stage,
                count: Number(edition[stage.key] || 0),
              }));
              const barTotal = counts.reduce((sum, s) => sum + s.count, 0);
              const declined = Number(edition.declined || 0);
              return (
                <Box key={edition.edition_year}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Year {edition.edition_year}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Number(edition.total || 0).toLocaleString()} book
                      {Number(edition.total || 0) === 1 ? '' : 's'}
                    </Typography>
                  </Box>

                  {barTotal > 0 && (
                    <Box
                      role="img"
                      aria-label={counts
                        .filter((s) => s.count > 0)
                        .map((s) => `${s.label}: ${s.count}`)
                        .join(', ')}
                      sx={{ display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden', mb: 1 }}
                    >
                      {counts
                        .filter((s) => s.count > 0)
                        .map((s) => (
                          <Tooltip key={String(s.key)} title={`${s.label}: ${s.count}`}>
                            <Box
                              sx={{
                                width: `${(s.count / barTotal) * 100}%`,
                                backgroundColor: theme.palette[s.tone].main,
                              }}
                            />
                          </Tooltip>
                        ))}
                    </Box>
                  )}

                  <Stack spacing={0.5}>
                    {counts.map((s) => (
                      <Box
                        key={String(s.key)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            aria-hidden
                            sx={{
                              width: 8, height: 8, borderRadius: '50%', flex: 'none',
                              backgroundColor: theme.palette[s.tone].main,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {s.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.count.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  {declined > 0 && (
                    /*
                      NEUTRAL, not error-toned: a decline is an honoured
                      decision, and the pipeline never asks that couple again.
                    */
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {declined.toLocaleString()} declined — honoured, and never asked again
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {declined.toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  {edition.oldest_open_invite_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Oldest unanswered invitation: {shortDate(edition.oldest_open_invite_at)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {pipeline && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>{pipeline.invite_due_now.toLocaleString()}</strong> eligible to be invited now ·{' '}
            <strong>{pipeline.upcoming_90d.toLocaleString()}</strong> becoming eligible in the next 90
            days
            {pipeline.oldest_due_wedding_date
              ? ` · oldest wedding date ${shortDate(pipeline.oldest_due_wedding_date)}`
              : ''}
          </Typography>
        )}

        {anniversary.oldest_open_invite_days !== null
          && anniversary.oldest_open_invite_days !== undefined && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The oldest unanswered invitation has been open{' '}
            {anniversary.oldest_open_invite_days.toLocaleString()} day
            {anniversary.oldest_open_invite_days === 1 ? '' : 's'}.
          </Typography>
        )}

        {/* The server's sentence, verbatim: eligibility, not scheduled sends. */}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          {anniversary.eligibility_note}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AnniversaryProgrammeCard;
